import React, { useState } from "react";
import { motion } from "framer-motion";

const features = [
  {
    icon: "shield-alt",
    title: "Safety First Approach",
    description:
      "Our elevators exceed industry safety standards with advanced monitoring systems and redundant safety features for peace of mind.",
    color: "#1a56db",
  },
  {
    icon: "leaf",
    title: "Energy Efficient",
    description:
      "Eco-friendly designs that reduce power consumption by up to 40% compared to conventional elevators, saving costs and the environment.",
    color: "#047857",
  },
  {
    icon: "tools",
    title: "Lifetime Support",
    description:
      "24/7 maintenance and rapid response teams ensure your elevator systems operate flawlessly with minimal downtime.",
    color: "#b91c1c",
  },
];

const additionalFeatures = [
  {
    text: "Smart control systems",
    icon: "microchip",
  },
  {
    text: "Custom design options",
    icon: "paint-brush",
  },
  {
    text: "5-year warranty",
    icon: "certificate",
  },
];

// Simple animation wrapper component
const AnimatedSection = ({ children, animation, delay = 0 }) => {
  const animations = {
    fadeInUp: {
      initial: { opacity: 0, y: 30 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.7, delay },
    },
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.7, delay },
    },
  };

  const selectedAnimation = animations[animation] || animations.fadeIn;

  return (
    <motion.div
      initial={selectedAnimation.initial}
      animate={selectedAnimation.animate}
      transition={selectedAnimation.transition}
    >
      {children}
    </motion.div>
  );
};

const FeatureCard = ({ feature, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <AnimatedSection key={index} animation="fadeInUp" delay={index * 0.2}>
      <motion.div
        style={{
          ...styles.featureCard,
          transform: isHovered ? "translateY(-15px)" : "translateY(0)",
          boxShadow: isHovered
            ? "0 20px 40px rgba(0, 0, 0, 0.12)"
            : "0 10px 30px rgba(0, 0, 0, 0.08)",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{
            ...styles.iconBox,
            backgroundColor: feature.color,
          }}
          animate={{
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 5 : 0,
          }}
          transition={{ duration: 0.4, type: "spring" }}
        >
          <i
            className={`fas fa-${feature.icon}`}
            style={styles.featureIcon}
          ></i>
        </motion.div>

        <h3 style={styles.featureTitle}>{feature.title}</h3>

        <motion.div
          style={{
            ...styles.featureDivider,
            backgroundColor: isHovered ? feature.color : "#e2e8f0",
          }}
          animate={{
            width: isHovered ? "80px" : "50px",
          }}
          transition={{ duration: 0.4 }}
        ></motion.div>

        <p style={styles.featureDescription}>{feature.description}</p>
      </motion.div>
    </AnimatedSection>
  );
};

const WhyChooseUs = () => {
  return (
    <section style={styles.section}>
      {/* Background accent elements */}
      <div style={styles.backgroundAccent1}></div>
      <div style={styles.backgroundAccent2}></div>

      <div style={styles.sectionContainer}>
        <AnimatedSection animation="fadeIn">
          <div style={styles.titleContainer}>
            <h2 style={styles.sectionTitle}>
              Why Choose <span style={styles.highlight}>Our Elevators</span>
            </h2>
            <div style={styles.titleUnderline}>
              <div style={styles.titleUnderlinePrimary}></div>
            </div>
            <p style={styles.sectionSubtitle}>
              We combine cutting-edge technology with exceptional service to
              deliver elevator solutions that exceed expectations
            </p>
          </div>
        </AnimatedSection>

        <div style={styles.featuresContainer}>
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>

        <AnimatedSection animation="fadeInUp" delay={0.4}>
          <div style={styles.additionalFeaturesWrapper}>
            <h3 style={styles.additionalFeaturesTitle}>Additional Benefits</h3>
            <div style={styles.additionalFeatures}>
              {additionalFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  style={styles.additionalFeatureItem}
                  whileHover={{
                    scale: 1.03,
                    backgroundColor: "rgba(26, 86, 219, 0.05)",
                    borderColor: "#1a56db",
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div style={styles.featureIconContainer}>
                    <i
                      className={`fas fa-${feature.icon}`}
                      style={styles.additionalFeatureIcon}
                    ></i>
                  </motion.div>
                  <span style={styles.featureText}>{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: "100px 0 40px",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#fcfcfd",
  },
  backgroundAccent1: {
    position: "absolute",
    width: "600px",
    height: "600px",
    backgroundColor: "rgba(26, 86, 219, 0.03)",
    borderRadius: "50%",
    top: "-300px",
    left: "-100px",
    zIndex: 0,
  },
  backgroundAccent2: {
    position: "absolute",
    width: "400px",
    height: "400px",
    backgroundColor: "rgba(4, 120, 87, 0.03)",
    borderRadius: "50%",
    bottom: "-200px",
    right: "-100px",
    zIndex: 0,
  },
  sectionContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
    zIndex: 1,
  },
  titleContainer: {
    textAlign: "center",
    marginBottom: "60px",
  },
  sectionTitle: {
    fontSize: "42px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "16px",
    "@media (max-width: 768px)": {
      fontSize: "32px",
    },
  },
  highlight: {
    color: "#1a56db",
  },
  titleUnderline: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "20px",
  },
  titleUnderlinePrimary: {
    width: "80px",
    height: "4px",
    backgroundColor: "#1a56db",
    borderRadius: "2px",
  },
  sectionSubtitle: {
    fontSize: "18px",
    color: "#64748b",
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: "1.6",
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
  },
  featuresContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "30px",
    marginBottom: "60px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  featureCard: {
    backgroundColor: "white",
    borderRadius: "20px",
    padding: "40px 30px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  iconBox: {
    width: "70px",
    height: "70px",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "25px",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
  },
  featureIcon: {
    fontSize: "28px",
    color: "white",
  },
  featureTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "15px",
  },
  featureDivider: {
    width: "50px",
    height: "3px",
    backgroundColor: "#e2e8f0",
    borderRadius: "1.5px",
    marginBottom: "20px",
    transition: "all 0.4s ease",
  },
  featureDescription: {
    fontSize: "16px",
    color: "#64748b",
    lineHeight: "1.6",
    marginBottom: "25px",
    flex: "1 1 auto",
  },
  arrowIcon: {
    fontSize: "14px",
    color: "#1a56db",
  },
  additionalFeaturesWrapper: {
    backgroundColor: "white",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
    marginBottom: "60px",
  },
  additionalFeaturesTitle: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "25px",
    textAlign: "center",
  },
  additionalFeatures: {
    display: "grid",  
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "100px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },
  additionalFeatureItem: {
    display: "flex",
    alignItems: "center",
    padding: "16px 20px",
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "all 0.3s ease",
  },
  featureIconContainer: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "rgba(26, 86, 219, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "15px",
    flexShrink: 0,
  },
  additionalFeatureIcon: {
    color: "#1a56db",
    fontSize: "16px",
  },
  featureText: {
    fontSize: "15px",
    fontWeight: "500",
    color: "#334155",
  },
  ctaContainer: {
    textAlign: "center",
    backgroundColor: "#1a56db",
    borderRadius: "20px",
    padding: "60px 40px",
    background: "linear-gradient(135deg, #1a56db 0%, #3182ce 100%)",
    boxShadow: "0 20px 40px rgba(26, 86, 219, 0.2)",
    color: "white",
    "@media (max-width: 768px)": {
      padding: "40px 20px",
    },
  },
  ctaTitle: {
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "15px",
    "@media (max-width: 768px)": {
      fontSize: "26px",
    },
  },
  ctaDescription: {
    fontSize: "18px",
    opacity: 0.9,
    maxWidth: "700px",
    margin: "0 auto 30px",
    lineHeight: "1.6",
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
  },
  ctaButton: {
    backgroundColor: "white",
    color: "#1a56db",
    border: "none",
    borderRadius: "12px",
    padding: "16px 32px",
    fontSize: "17px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  ctaButtonIcon: {
    marginLeft: "10px",
    fontSize: "14px",
  },
};

export default WhyChooseUs;
